import { Component, Input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ApexChart, ApexDataLabels, ApexLegend, ApexNonAxisChartSeries, ApexPlotOptions, ApexStroke, NgApexchartsModule
} from 'ng-apexcharts';
import { ApiEnvironmentDeltaService, EnvAreas, EnvAreasDetailModel, EnvDeltaOrganizationModel } from '../../../../shared/services/api-envdelta.service';
import { Environments } from '../../../../shared/models/environments.type';
import { DropdownComponent } from '../../../../shared/components/ui/dropdown/dropdown.component';
import { DropdownItemComponent } from '../../../../shared/components/ui/dropdown/dropdown-item/dropdown-item.component';

@Component({
  selector: 'app-env-delta-status-card',
  imports: [CommonModule, NgApexchartsModule, DropdownComponent, DropdownItemComponent],
  templateUrl: './env-delta-status-card.component.html',
})
export class EnvDeltaStatusCardComponent implements OnInit {
  @Input({ required: true }) source!: Environments;
  @Input({ required: true }) destination!: Environments;
  // When set, the card shows just this one organization's status/detail instead of the donut +
  // top-organizations breakdown across all of them. Data is already fetched for every organization
  // regardless, so switching this doesn't need a re-fetch — it's a display-only filter.
  @Input() organizationId?: string;

  // Fixed, known order/labels for the areas this widget cares about — only the ones the API
  // actually returned (`presentAreas`) are rendered, in this order.
  private readonly areaOrder: EnvAreas[] = ['organization', 'surveys', 'alerts'];
  readonly areaLabels: Record<EnvAreas, string> = { organization: 'Organization', surveys: 'Survey', alerts: 'Alerts' };
  presentAreas: EnvAreas[] = [];

  loading = signal(false);
  error = signal<string | null>(null);
  inSyncCount = signal(0);
  withDeltaCount = signal(0);
  skippedCount = signal(0);
  failedCount = signal(0);

  organizations: EnvDeltaOrganizationModel[] = [];

  series: ApexNonAxisChartSeries = [0, 0];
  readonly chart: ApexChart = {
    type: 'donut',
    height: 180,
    sparkline: { enabled: true },
  };
  readonly colors: string[] = ['#12B76A', '#F79009'];
  readonly labels: string[] = ['In Sync', 'With Delta'];
  readonly dataLabels: ApexDataLabels = { enabled: false };
  readonly stroke: ApexStroke = { width: 0 };
  readonly legend: ApexLegend = { show: false };
  plotOptions: ApexPlotOptions = {};
  dropdownIsOpen = false;

  constructor(private environmentDeltaService: ApiEnvironmentDeltaService) {}

  ngOnInit(): void {
    this.plotOptions = {
      pie: {
        donut: {
          size: '75%',
          labels: {
            show: true,
            name: { show: false },
            value: {
              fontSize: '22px',
              fontWeight: 600,
              offsetY: 6,
              formatter: () => String(this.inSyncCount() + this.withDeltaCount()),
            },
          },
        },
      },
    };
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.environmentDeltaService.getEnvironmentsDeltaFull(this.source, this.destination).subscribe({
      next: (result) => {
        const inSync = Math.max(0,
          result.summary.organizationsAnalysed
          - result.summary.organizationsWithDelta
          - result.summary.organizationsSkippedObsolete
          - result.summary.organizationsFailed
        );
        this.inSyncCount.set(inSync);
        this.withDeltaCount.set(result.summary.organizationsWithDelta);
        this.skippedCount.set(result.summary.organizationsSkippedObsolete);
        this.failedCount.set(result.summary.organizationsFailed);
        this.series = [inSync, result.summary.organizationsWithDelta];
        this.presentAreas = this.areaOrder.filter(a => result.areas.includes(a));
        this.organizations = result.organizations;
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load delta status. | ' + err);
        this.loading.set(false);
      }
    });
  }

  get areaBreakdownLabel(): string {
    return this.presentAreas.map(a => this.areaLabels[a]).join(' / ');
  }

  get selectedOrganization(): EnvDeltaOrganizationModel | undefined {
    return this.organizationId ? this.organizations.find(o => o.organizationId === this.organizationId) : undefined;
  }

  get topDeltaOrganizations(): EnvDeltaOrganizationModel[] {
    return this.organizations
      .filter(o => o.hasDelta && !o.error)
      .sort((a, b) => this.totalChanges(b) - this.totalChanges(a));
      //.slice(0, 3);
  }

  totalChanges(org: EnvDeltaOrganizationModel): number {
    return org.areas.reduce((sum, a) => sum + a.changes, 0);
  }

  getArea(org: EnvDeltaOrganizationModel, key: EnvAreas): EnvAreasDetailModel | undefined {
    return org.areas.find(a => a.areaKey === key);
  }

  // Scales this organization's bars against its own busiest area, capped at 8 so one outlier
  // area doesn't flatten the other bars in the same card.
  orgChartMax(org: EnvDeltaOrganizationModel): number {
    const raw = Math.max(1, ...org.areas.map(a => a.changes));
    return Math.min(Math.ceil(raw), 8);
  }

  axisTicks(max: number): number[] {
    return Array.from({ length: max + 1 }, (_, i) => max - i);
  }

  segmentHeightPercent(count: number, max: number): number {
    return max > 0 ? Math.min(100, (count / max) * 100) : 0;
  }

  toggleDropdown(): void {
    this.dropdownIsOpen = !this.dropdownIsOpen;
  }

  closeDropdown(): void {
    this.dropdownIsOpen = false;
  }

  refresh(): void {
    this.closeDropdown();
    this.load();
  }

}
